/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import { useQuery } from '@tanstack/react-query';
import {
  getAuditLogActionsByDatabaseId,
  getAuditLogActionsByEntityAssociation,
  getAuditLogsByDatabaseId,
  getAuditLogsByDatabaseIdPaginated,
  getAuditLogsByGroupIdPaginated,
  getAuditLogsByEntityId,
  getAuditLogsByEntityAssociationPaginated,
  getAuditLogsByEntityIdPaginated,
  getAuditLogsByProductId,
  getAuditLogsByProductIdPaginated,
  getAuditLogsByTariffId
} from './audit.service';
import { ScopedQuery, Time, toScopedQuery } from '../shared';
import { DatabaseEntityId, EntityId } from '../ids';
import { IAuditLogDatabaseFilters, IAuditLogPageResponse } from './audit.d';

// Query key constants
const QUERY_KEYS = {
  AUDIT: 'audit',
  AUDIT_LOGS: 'audit-logs',
  BY_DATABASE: 'by-database',
  BY_DATABASE_PAGINATED: 'by-database-paginated',
  BY_GROUP_PAGINATED: 'by-group-paginated',
  BY_DATABASE_ACTIONS: 'by-database-actions',
  BY_ENTITY: 'by-entity',
  BY_ENTITY_PAGINATED: 'by-entity-paginated',
  BY_ENTITY_ASSOCIATION_PAGINATED: 'by-entity-association-paginated',
  BY_ENTITY_ASSOCIATION_ACTIONS: 'by-entity-association-actions',
  BY_PRODUCT: 'by-product',
  BY_PRODUCT_PAGINATED: 'by-product-paginated',
  BY_TARIFF: 'by-tariff',
} as const;

const auditKeys = {
  all: () => [ QUERY_KEYS.AUDIT ] as const,
  auditLogs: () => [ ...auditKeys.all(), QUERY_KEYS.AUDIT_LOGS ] as const,
  auditLogsByDatabase: (databaseId: string, limit?: number, offset?: number, includeErrors?: boolean) =>
    [
      ...auditKeys.auditLogs(),
      QUERY_KEYS.BY_DATABASE,
      databaseId,
      { limit: limit, offset: offset, includeErrors: includeErrors },
    ] as const,
  auditLogsByDatabasePaginated: (
    databaseId: string,
    limit?: number,
    offset?: number,
    includeErrors?: boolean,
    filters?: IAuditLogDatabaseFilters,
  ) =>
    [
      ...auditKeys.auditLogs(),
      QUERY_KEYS.BY_DATABASE_PAGINATED,
      databaseId,
      { limit: limit, offset: offset, includeErrors: includeErrors, filters: filters },
    ] as const,
  auditLogActionsByDatabase: (databaseId: string, selectedTeamId?: string) =>
    [
      ...auditKeys.auditLogs(),
      QUERY_KEYS.BY_DATABASE_ACTIONS,
      databaseId,
      selectedTeamId,
    ] as const,
  auditLogsByGroupPaginated: (
    groupId: string,
    limit?: number,
    offset?: number,
    includeErrors?: boolean,
    databaseType?: string,
  ) =>
    [
      ...auditKeys.auditLogs(),
      QUERY_KEYS.BY_GROUP_PAGINATED,
      groupId,
      { limit: limit, offset: offset, includeErrors: includeErrors, databaseType: databaseType },
    ] as const,
  auditLogsByEntity: (entityId: string, limit?: number, offset?: number) =>
    [
      ...auditKeys.auditLogs(),
      QUERY_KEYS.BY_ENTITY,
      entityId,
      { limit: limit, offset: offset },
    ] as const,
  auditLogsByEntityPaginated: (
    entityId: string,
    limit?: number,
    offset?: number,
  ) =>
    [
      ...auditKeys.auditLogs(),
      QUERY_KEYS.BY_ENTITY_PAGINATED,
      entityId,
      { limit: limit, offset: offset },
    ] as const,
  auditLogsByEntityAssociationPaginated: (
    entityId: string,
    databaseEntityId: string,
    databaseId: string,
    selectedTeamId: string,
    limit?: number,
    offset?: number,
    filters?: IAuditLogDatabaseFilters,
  ) =>
    [
      ...auditKeys.auditLogs(),
      QUERY_KEYS.BY_ENTITY_ASSOCIATION_PAGINATED,
      databaseId,
      databaseEntityId,
      entityId,
      selectedTeamId,
      { limit: limit, offset: offset, filters: filters },
    ] as const,
  auditLogActionsByEntityAssociation: (
    entityId: string,
    databaseEntityId: string,
    databaseId: string,
    selectedTeamId: string,
  ) =>
    [
      ...auditKeys.auditLogs(),
      QUERY_KEYS.BY_ENTITY_ASSOCIATION_ACTIONS,
      databaseId,
      databaseEntityId,
      entityId,
      selectedTeamId,
    ] as const,
  auditLogsByTariff: (tariffId: string, limit?: number, offset?: number) =>
    [
      ...auditKeys.auditLogs(),
      QUERY_KEYS.BY_TARIFF,
      tariffId,
      { limit: limit, offset: offset },
    ] as const,
  auditLogsByProduct: (productId: string, limit?: number, offset?: number) =>
    [
      ...auditKeys.auditLogs(),
      QUERY_KEYS.BY_PRODUCT,
      productId,
      { limit: limit, offset: offset },
    ] as const,
  auditLogsByProductPaginated: (
    productId: string,
    limit?: number,
    offset?: number,
  ) =>
    [
      ...auditKeys.auditLogs(),
      QUERY_KEYS.BY_PRODUCT_PAGINATED,
      productId,
      { limit: limit, offset: offset },
    ] as const,
};

export const useGetAuditLogsByDatabaseId = (
  databaseId: string,
  limit = 50,
  offset = 0,
  includeErrors = false,
) => {
  return useQuery({
    queryKey: auditKeys.auditLogsByDatabase(databaseId, limit, offset, includeErrors),
    queryFn: () => getAuditLogsByDatabaseId(databaseId, limit, offset, includeErrors),
    staleTime: 5 * Time.MINUTE,
    gcTime: 10 * Time.MINUTE,
    enabled: Boolean(databaseId),
  });
};

export const useGetAuditLogsByDatabaseIdPaginated = (
  databaseId: string,
  limit = 10,
  offset = 0,
  includeErrors = false,
  filters?: IAuditLogDatabaseFilters,
  options: { enabled?: boolean } = {},
) => {
  return useQuery({
    queryKey: auditKeys.auditLogsByDatabasePaginated(databaseId, limit, offset, includeErrors, filters),
    queryFn: () => getAuditLogsByDatabaseIdPaginated(databaseId, limit, offset, includeErrors, filters),
    staleTime: 1 * Time.MINUTE,
    gcTime: 5 * Time.MINUTE,
    enabled: Boolean(databaseId) && (options.enabled ?? true),
  });
};

export const useGetAuditLogActionsByDatabaseId = (
  databaseId: string,
  selectedTeamId: string | undefined,
  options: { enabled?: boolean } = {},
) => {
  return useQuery({
    queryKey: auditKeys.auditLogActionsByDatabase(databaseId, selectedTeamId),
    queryFn: () =>
      getAuditLogActionsByDatabaseId(databaseId, selectedTeamId as string),
    staleTime: 5 * Time.MINUTE,
    gcTime: 10 * Time.MINUTE,
    enabled:
      Boolean(databaseId) && Boolean(selectedTeamId) && (options.enabled ?? true),
  });
};

export const useGetAuditLogsByGroupIdPaginated = (
  groupId: string,
  limit = 10,
  offset = 0,
  includeErrors = false,
  databaseType?: string,
) => {
  return useQuery({
    queryKey: auditKeys.auditLogsByGroupPaginated(groupId, limit, offset, includeErrors, databaseType),
    queryFn: () => getAuditLogsByGroupIdPaginated(groupId, limit, offset, includeErrors, databaseType),
    staleTime: 1 * Time.MINUTE,
    gcTime: 5 * Time.MINUTE,
    enabled: Boolean(groupId),
  });
};

export const useGetAuditLogsByEntityId = (
  entityId: string,
  limit = 50,
  offset = 0,
) => {
  return useQuery({
    queryKey: auditKeys.auditLogsByEntity(entityId, limit, offset),
    queryFn: () => getAuditLogsByEntityId(entityId, limit, offset),
    staleTime: 5 * Time.MINUTE,
    gcTime: 10 * Time.MINUTE,
    enabled: Boolean(entityId),
  });
};

export const useGetAuditLogsByEntityIdPaginated = (
  entityId: string,
  limit = 5,
  offset = 0,
) => {
  return useQuery({
    queryKey: auditKeys.auditLogsByEntityPaginated(entityId, limit, offset),
    queryFn: () => getAuditLogsByEntityIdPaginated(entityId, limit, offset),
    staleTime: 1 * Time.MINUTE,
    gcTime: 5 * Time.MINUTE,
    enabled: Boolean(entityId),
  });
};

export const useGetAuditLogsByTariffId = (
  tariffId: string,
  limit = 50,
  offset = 0,
) => {
  return useQuery({
    queryKey: auditKeys.auditLogsByTariff(tariffId, limit, offset),
    queryFn: () => getAuditLogsByTariffId(tariffId, limit, offset),
    staleTime: 5 * Time.MINUTE,
    gcTime: 10 * Time.MINUTE,
    enabled: Boolean(tariffId),
  });
};

export const useGetAuditLogsByProductId = (
  productId: string,
  limit = 50,
  offset = 0,
) => {
  return useQuery({
    queryKey: auditKeys.auditLogsByProduct(productId, limit, offset),
    queryFn: () => getAuditLogsByProductId(productId, limit, offset),
    staleTime: 5 * Time.MINUTE,
    gcTime: 10 * Time.MINUTE,
    enabled: Boolean(productId),
  });
};

export const useGetAuditLogsByProductIdPaginated = (
  productId: string,
  limit = 5,
  offset = 0,
) => {
  return useQuery({
    queryKey: auditKeys.auditLogsByProductPaginated(productId, limit, offset),
    queryFn: () => getAuditLogsByProductIdPaginated(productId, limit, offset),
    staleTime: 1 * Time.MINUTE,
    gcTime: 5 * Time.MINUTE,
    enabled: Boolean(productId),
  });
};

export const useEntityTimeline = (
  ref: { entityId: EntityId; databaseEntityId: DatabaseEntityId } | null,
  databaseId: string | undefined,
  selectedTeamId: string | undefined,
  shouldRun: boolean,
  limit = 5,
  offset = 0,
  filters?: IAuditLogDatabaseFilters,
): ScopedQuery<IAuditLogPageResponse> => {
  const entityId = ref ? String(ref.entityId) : '';
  const databaseEntityId = ref ? String(ref.databaseEntityId) : '';
  const scopedDatabaseId = databaseId ? String(databaseId) : '';
  const scopedTeamId = selectedTeamId ? String(selectedTeamId) : '';
  const enabled = Boolean(
    shouldRun &&
      entityId &&
      databaseEntityId &&
      scopedDatabaseId &&
      scopedTeamId,
  );

  const query = useQuery({
    queryKey: auditKeys.auditLogsByEntityAssociationPaginated(
      entityId,
      databaseEntityId,
      scopedDatabaseId,
      scopedTeamId,
      limit,
      offset,
      filters,
    ),
    queryFn: () =>
      getAuditLogsByEntityAssociationPaginated(
        entityId,
        databaseEntityId,
        scopedDatabaseId,
        scopedTeamId,
        limit,
        offset,
        filters,
      ),
    staleTime: 1 * Time.MINUTE,
    gcTime: 5 * Time.MINUTE,
    refetchOnMount: 'always',
    retry: false,
    enabled: enabled,
  });

  return toScopedQuery(query, {
    enabled: enabled,
    isEmpty: (page) => (page.pagination?.totalItems || 0) === 0,
  });
};

export const useGetAuditLogActionsByEntityAssociation = (
  ref: { entityId: EntityId; databaseEntityId: DatabaseEntityId } | null,
  databaseId: string | undefined,
  selectedTeamId: string | undefined,
) => {
  const entityId = ref ? String(ref.entityId) : '';
  const databaseEntityId = ref ? String(ref.databaseEntityId) : '';
  const scopedDatabaseId = databaseId ? String(databaseId) : '';
  const scopedTeamId = selectedTeamId ? String(selectedTeamId) : '';

  return useQuery({
    queryKey: auditKeys.auditLogActionsByEntityAssociation(
      entityId,
      databaseEntityId,
      scopedDatabaseId,
      scopedTeamId,
    ),
    queryFn: () =>
      getAuditLogActionsByEntityAssociation(
        entityId,
        databaseEntityId,
        scopedDatabaseId,
        scopedTeamId,
      ),
    staleTime: 5 * Time.MINUTE,
    gcTime: 10 * Time.MINUTE,
    enabled: Boolean(entityId && databaseEntityId && scopedDatabaseId && scopedTeamId),
  });
};

// Export audit keys for potential use in other parts of the application
export { auditKeys };
